
import { useParams, useNavigate } from "react-router-dom";
import { Shell } from "@/components/layout/Shell";
import { CategoryForm } from "@/components/categories/CategoryForm";
import { useCategories } from "@/hooks/useCategories";
import { Skeleton } from "@/components/ui/skeleton";
import { toast } from "sonner";
import { Category } from "@/lib/types";

export default function CategoryEditPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { useCategoryQuery } = useCategories();
  
  const { 
    data: category, 
    isLoading, 
    isError 
  } = useCategoryQuery(id || "");
  
  const handleSuccess = (category: Category) => {
    toast.success("Category updated successfully");
    navigate("/categories");
  };

  const handleCancel = () => {
    navigate("/categories");
  };

  if (isLoading) {
    return (
      <Shell>
        <div className="space-y-6">
          <div>
            <Skeleton className="h-10 w-64" />
            <Skeleton className="h-4 w-32 mt-2" />
          </div>
          <div className="space-y-8">
            <Skeleton className="h-40 w-full" />
          </div>
        </div>
      </Shell>
    );
  }

  if (isError || !category) {
    return (
      <Shell>
        <div className="p-6 bg-destructive/10 rounded-md">
          <h1 className="text-xl font-medium text-destructive">Error loading category</h1>
          <p className="mt-2">
            Could not load the category. It might have been deleted or you don't have
            permission to view it.
          </p>
          <button
            onClick={() => navigate("/categories")}
            className="mt-4 px-4 py-2 bg-primary text-primary-foreground rounded-md"
          >
            Back to Categories
          </button>
        </div>
      </Shell>
    );
  }

  return (
    <Shell>
      <div className="space-y-6">
        <div>
          <h1 className="text-3xl font-bold tracking-tight dark:text-white">Edit Category</h1>
          <p className="text-muted-foreground mt-2 dark:text-slate-400">
            Update category details for {category.name}
          </p>
        </div>

        <CategoryForm 
          category={category} 
          mode="edit" 
          onSuccess={handleSuccess}
          onCancel={handleCancel}
        />
      </div>
    </Shell>
  );
}
